import fromPairs from 'lodash/fromPairs';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';

import {
    createAliceAgentAppUserAgreement,
    deleteAliceAgentAppUserAgreement,
    reorderAliceAgentAppUserAgreements,
    updateAliceAgentAppUserAgreement,
} from '../api/services/aliceAgentApps';
import {
    createUserAgreement,
    deleteUserAgreement,
    reorderAgreements,
    updateUserAgreement,
} from '../api/services/userAgreement';
import { objectsDiff } from '../lib/utils';

import { Channel } from './skill';

export type UserAgreementDraft = {
    name: string;
    url: string;
    order: number;
};

export type UserAgreement = UserAgreementDraft & {
    id: string;
};

export type UserAgreementOrDraft = UserAgreement | UserAgreementDraft;

export const formUARequests = async (
    skillId: string,
    fetched: UserAgreement[],
    drafts: UserAgreementOrDraft[],
    channel: Channel,
) => {
    const fetchMap = new Map(fetched.map((item) => [item.id, item]));
    const orderedIds: string[] = [];
    for (const draft of drafts) {
        const unordered = omit(draft, 'order');
        if ('id' in unordered && typeof unordered['id'] === 'string') {
            if (objectsDiff(fetchMap.get(unordered.id), unordered).length > 0) {
                await updateUserAgreement(unordered, skillId, channel);
                orderedIds.push(unordered.id);
            }
            fetchMap.delete(unordered.id);
        } else {
            const { id } = await createUserAgreement(unordered, skillId, channel);
            orderedIds.push(id);
        }
    }
    for (const item of fetchMap.values()) {
        await deleteUserAgreement(item, skillId, channel);
    }
    if (orderedIds.length) {
        const reordered = fromPairs(orderedIds.map((id, idx) => [id, idx]));
        await reorderAgreements(skillId, reordered, channel);
    }
};

/**
 * Same diff-and-sync flow as `formUARequests`, but for alice-agent-apps (v3 endpoints, no channel).
 */
export const syncAliceAgentAppUserAgreements = async (
    skillId: string,
    fetched: UserAgreement[],
    drafts: UserAgreementOrDraft[],
): Promise<string[]> => {
    const fetchMap = new Map(fetched.map((item) => [item.id, item]));
    const orderedIds: string[] = [];
    for (const draft of drafts) {
        const unordered = omit(draft, 'order');
        if ('id' in unordered && typeof unordered['id'] === 'string') {
            if (objectsDiff(fetchMap.get(unordered.id), unordered).length > 0) {
                await updateAliceAgentAppUserAgreement(skillId, unordered.id, unordered);
                orderedIds.push(unordered.id);
            }
            fetchMap.delete(unordered.id);
        } else {
            const { id } = await createAliceAgentAppUserAgreement(skillId, unordered);
            orderedIds.push(id);
        }
    }
    for (const item of fetchMap.values()) {
        await deleteAliceAgentAppUserAgreement(skillId, item.id);
    }
    if (orderedIds.length) {
        const reordered = fromPairs(orderedIds.map((id, idx) => [id, idx]));
        await reorderAliceAgentAppUserAgreements(skillId, reordered);
    }
    return orderedIds;
};

export const sortUserAgreements = (list: UserAgreement[]): UserAgreement[] =>
    [...list].sort((a, b) => a.order - b.order);

/**
 * Stamps server ids onto no-id form rows that already got persisted during a partially-failed
 * save batch. A created row is echoed back verbatim by the backend (unique `name` and `url` per
 * skill), so an exact name+url match is an unambiguous key.
 *
 * Used on the error path to keep the user's input on screen while ensuring a retry re-sends the
 * already-created rows as no-op updates (id present) instead of `create` — which would otherwise
 * hit the backend's unique-name/url 400 on a row the user never touched.
 */
export const reconcileAgreementIds = (
    current: UserAgreementOrDraft[],
    serverSnapshot: UserAgreement[],
): UserAgreementOrDraft[] =>
    current.map((row) => {
        if (isPersistedAgreement(row)) return row;
        const match = serverSnapshot.find((s) => s.name === row.name && s.url === row.url);
        return match ? { ...row, id: match.id } : row;
    });

const isPersistedAgreement = (item: UserAgreementOrDraft): item is UserAgreement => 'id' in item;

/**
 * Syncs agent-app user agreements only when the form value differs from the baseline.
 *
 * The form field is typed as `UserAgreementOrDraft[]` (new rows have no id), but the server
 * baseline always holds persisted rows (with ids). Narrow it with a type guard instead of
 * casting at every call site, and centralize the change-detection here.
 */
export const syncUserAgreementsOnChange = async (
    skillId: string,
    baseline: UserAgreementOrDraft[],
    current: UserAgreementOrDraft[],
): Promise<void> => {
    if (isEqual(baseline, current)) return;
    await syncAliceAgentAppUserAgreements(skillId, baseline.filter(isPersistedAgreement), current);
};
